import { useState, useRef, useEffect, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Download,
  HardDrive,
  AlertTriangle,
  UploadCloud,
  RefreshCw,
  FileArchive,
  Shield,
  Clock,
} from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { formatDate } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';

// ─── Types ───────────────────────────────────────────────────────────────────

interface BackupEntry {
  filename: string;
  size_bytes: number;
  created_at: string;
}

interface BackupListResponse {
  data: {
    backups: BackupEntry[];
  };
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

// ─── Backup Card ─────────────────────────────────────────────────────────────

function BackupCard() {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['backups'],
    queryFn: () => apiFetch<BackupListResponse>('/api/backup/list'),
    staleTime: 30_000,
  });

  const backups = data?.data.backups ?? [];

  return (
    <Card>
      <CardHeader className="pb-4">
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-md bg-blue-50 text-blue-600">
            <HardDrive className="h-4 w-4" />
          </div>
          <div>
            <CardTitle className="text-base">Backup</CardTitle>
            <CardDescription className="text-xs mt-0.5">
              Download a fresh snapshot or retrieve a scheduled backup
            </CardDescription>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-5">
        {/* Download fresh backup */}
        <div className="flex flex-col sm:flex-row sm:items-start gap-4 p-4 rounded-lg bg-muted/40 border border-border/60">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium leading-tight">Fresh Snapshot</p>
            <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
              Creates a new backup right now — bundles the database, all attachments, and a
              settings manifest. An automatic backup also runs daily at 2:00 AM; the 7 most
              recent are kept below.
            </p>
          </div>
          <a
            href="/api/backup/download"
            download
            className={cn(
              'inline-flex items-center justify-center gap-1.5 shrink-0',
              'h-9 px-4 rounded-md text-sm font-medium',
              'bg-primary text-primary-foreground shadow',
              'hover:bg-primary/90 transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            )}
            aria-label="Download a fresh backup of the database and attachments"
          >
            <Download className="h-3.5 w-3.5" />
            Download Backup
          </a>
        </div>

        {/* Scheduled backup list */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Clock className="h-3.5 w-3.5 text-muted-foreground" />
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Saved Backups
            </p>
          </div>

          {isLoading && (
            <p className="text-sm text-muted-foreground py-6 text-center">
              Loading backups…
            </p>
          )}

          {isError && (
            <div className="rounded-md border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive">
              {error instanceof Error ? error.message : 'Failed to load backups'}
            </div>
          )}

          {!isLoading && !isError && backups.length === 0 && (
            <div className="rounded-lg border border-dashed border-border/70 py-8 text-center">
              <FileArchive className="h-7 w-7 text-muted-foreground/40 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No backups yet.</p>
              <p className="text-xs text-muted-foreground/70 mt-0.5">
                The first automatic backup runs tonight at 2:00 AM.
              </p>
            </div>
          )}

          {!isLoading && !isError && backups.length > 0 && (
            <div className="rounded-lg border overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="px-4 py-2.5 text-left text-xs font-medium text-muted-foreground">
                      Filename
                    </th>
                    <th className="px-4 py-2.5 text-right text-xs font-medium text-muted-foreground w-20">
                      Size
                    </th>
                    <th className="px-4 py-2.5 text-left text-xs font-medium text-muted-foreground w-28">
                      Created
                    </th>
                    <th className="px-4 py-2.5 text-right text-xs font-medium text-muted-foreground w-24">
                      &nbsp;
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {backups.map((b) => (
                    <tr key={b.filename} className="hover:bg-muted/30 transition-colors">
                      <td className="px-4 py-2.5 font-mono text-xs text-foreground truncate max-w-[260px]">
                        {b.filename}
                      </td>
                      <td className="px-4 py-2.5 text-right text-xs tabular-nums text-muted-foreground">
                        {formatBytes(b.size_bytes)}
                      </td>
                      <td className="px-4 py-2.5 text-xs text-muted-foreground">
                        {formatDate(b.created_at)}
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        <a
                          href={`/api/backup/download?file=${encodeURIComponent(b.filename)}`}
                          download
                          className={cn(
                            'inline-flex items-center gap-1 text-xs font-medium',
                            'text-primary hover:text-primary/80 hover:underline underline-offset-4',
                            'transition-colors',
                          )}
                          aria-label={`Download backup ${b.filename}`}
                        >
                          <Download className="h-3 w-3" />
                          Download
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Restore Card ─────────────────────────────────────────────────────────────

type RestorePhase = 'idle' | 'uploading' | 'restarting';

function RestoreCard() {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [confirmText, setConfirmText] = useState('');
  const [phase, setPhase] = useState<RestorePhase>('idle');
  const [uploadError, setUploadError] = useState<string | null>(null);

  const canRestore =
    phase === 'idle' && selectedFile !== null && confirmText === 'RESTORE';

  // ── Health polling (after a successful restore) ───────────────────────────

  const startHealthPoll = useCallback(() => {
    let cancelled = false;

    async function poll() {
      if (cancelled) return;
      try {
        const res = await fetch('/api/health', { credentials: 'include' });
        if (cancelled) return;
        if (res.ok) {
          window.location.href = '/login';
          return;
        }
      } catch {
        // Server is still down — expected during restart
      }
      if (!cancelled) {
        setTimeout(poll, 2000);
      }
    }

    poll();
    return () => {
      cancelled = true;
    };
  }, []);

  // Start polling immediately when phase transitions to 'restarting'
  useEffect(() => {
    if (phase !== 'restarting') return;
    const cancel = startHealthPoll();
    return cancel;
  }, [phase, startHealthPoll]);

  // ── File selection ────────────────────────────────────────────────────────

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0] ?? null;
    setSelectedFile(file);
    setUploadError(null);
  }

  // ── Restore submit ────────────────────────────────────────────────────────

  async function handleRestore() {
    if (!canRestore || !selectedFile) return;

    setPhase('uploading');
    setUploadError(null);

    try {
      const fd = new FormData();
      fd.append('file', selectedFile);

      // DO NOT set Content-Type — the browser sets the multipart boundary automatically
      const res = await fetch('/api/backup/restore', {
        method: 'POST',
        body: fd,
        credentials: 'include',
      });

      const body = await res.json().catch(() => ({})) as { data?: { restarting?: boolean }; error?: string };

      if (!res.ok) {
        throw new Error(body.error ?? 'Restore failed');
      }

      // 200 { data: { restarting: true } } — switch to restarting UI
      setPhase('restarting');
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Restore failed');
      setPhase('idle');
    }
  }

  // ── Restarting overlay ────────────────────────────────────────────────────

  if (phase === 'restarting') {
    return (
      <Card className="border-destructive/30">
        <CardContent className="py-16 flex flex-col items-center gap-5 text-center">
          <div className="relative">
            <div className="h-14 w-14 rounded-full border-2 border-destructive/20 flex items-center justify-center">
              <RefreshCw className="h-7 w-7 text-destructive animate-spin" />
            </div>
            {/* Ripple rings */}
            <span className="absolute inset-0 rounded-full border border-destructive/30 animate-ping" />
          </div>
          <div className="space-y-1.5">
            <p className="text-base font-semibold">Server is restarting</p>
            <p className="text-sm text-muted-foreground">
              Reconnecting — this page will redirect to the login screen once the
              server is back online.
            </p>
          </div>
          <p className="text-xs text-muted-foreground/60 font-mono tabular-nums">
            polling /api/health every 2 s…
          </p>
        </CardContent>
      </Card>
    );
  }

  // ── Normal restore form ───────────────────────────────────────────────────

  return (
    <Card className="border-destructive/20">
      <CardHeader className="pb-4">
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-md bg-red-50 text-red-600">
            <Shield className="h-4 w-4" />
          </div>
          <div>
            <CardTitle className="text-base text-destructive">Restore</CardTitle>
            <CardDescription className="text-xs mt-0.5">
              Replace all data from a backup archive
            </CardDescription>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-5">
        {/* Destructive warning banner */}
        <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-4">
          <div className="flex gap-3">
            <AlertTriangle className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
            <div className="space-y-1">
              <p className="text-sm font-semibold text-destructive leading-tight">
                This action cannot be undone
              </p>
              <p className="text-xs text-destructive/80 leading-relaxed">
                Restoring <strong>replaces all current data</strong> — the database and
                every attachment — with the contents of the backup file, and signs out all
                users. A pre-restore snapshot of the current state is saved automatically
                before anything is overwritten.
              </p>
            </div>
          </div>
        </div>

        {/* File picker */}
        <div className="space-y-2">
          <Label htmlFor="restore-file-input" className="text-sm font-medium">
            Backup file
          </Label>
          <div
            className={cn(
              'flex items-center gap-3 rounded-md border border-input px-3 py-2 cursor-pointer',
              'hover:border-muted-foreground/40 transition-colors',
              phase === 'uploading' && 'pointer-events-none opacity-60',
            )}
            onClick={() => fileInputRef.current?.click()}
          >
            <FileArchive className="h-4 w-4 text-muted-foreground shrink-0" />
            <span
              className={cn(
                'text-sm flex-1 truncate',
                selectedFile ? 'text-foreground' : 'text-muted-foreground',
              )}
            >
              {selectedFile ? selectedFile.name : 'Choose a .zip backup file…'}
            </span>
            <span className="text-xs text-muted-foreground shrink-0 border border-border rounded px-1.5 py-0.5">
              Browse
            </span>
          </div>
          <input
            ref={fileInputRef}
            id="restore-file-input"
            type="file"
            accept=".zip,application/zip"
            className="sr-only"
            aria-label="Select a .zip backup file to restore"
            onChange={handleFileChange}
            disabled={phase === 'uploading'}
          />
          {selectedFile && (
            <p className="text-xs text-muted-foreground pl-0.5">
              {formatBytes(selectedFile.size)} selected
            </p>
          )}
        </div>

        {/* Typed confirmation */}
        <div className="space-y-2">
          <Label htmlFor="restore-confirm-input" className="text-sm font-medium">
            Type{' '}
            <code className="font-mono text-destructive bg-destructive/10 rounded px-1 py-0.5 text-xs">
              RESTORE
            </code>{' '}
            to confirm
          </Label>
          <Input
            id="restore-confirm-input"
            aria-label="Type RESTORE to confirm the restore operation"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="RESTORE"
            disabled={phase === 'uploading'}
            autoComplete="off"
            spellCheck={false}
            className={cn(
              'font-mono tracking-widest',
              confirmText === 'RESTORE' &&
                'border-destructive/50 bg-destructive/5 text-destructive focus-visible:ring-destructive/50',
            )}
          />
        </div>

        {/* Upload error */}
        {uploadError && (
          <div className="rounded-md border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive">
            {uploadError}
          </div>
        )}

        {/* Submit */}
        <div className="flex items-center justify-end pt-1">
          <Button
            type="button"
            variant="destructive"
            onClick={handleRestore}
            disabled={!canRestore}
            className="gap-2 min-w-[140px]"
          >
            {phase === 'uploading' ? (
              <>
                <RefreshCw className="h-4 w-4 animate-spin" />
                Uploading…
              </>
            ) : (
              <>
                <UploadCloud className="h-4 w-4" />
                Restore Backup
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function BackupRestorePage() {
  return (
    <div className="p-6 max-w-3xl">
      {/* Page header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold tracking-tight">Backup & Restore</h1>
        <p className="text-sm text-muted-foreground mt-0.5">
          Download point-in-time snapshots or restore the application from a backup archive
        </p>
      </div>

      <div className="space-y-6">
        <BackupCard />
        <RestoreCard />
      </div>
    </div>
  );
}
